import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Table } from '@/components/ui/Table';
import { Pagination } from '@/components/ui/Pagination';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { usePrompt } from '@/components/ui/usePrompt';
import { cancelDo, dosExportPath, listDos, type DispatchOrder } from '@/api/dispatchOrder';
import { formatDate, formatINRSymbol } from '@/lib/format';
import { downloadExcel } from '@/lib/downloadExcel';
import { apiError } from '@/lib/apiError';
import { PromoteEnquiryModal } from './PromoteEnquiryModal';
import { DoDetailModal } from './DoDetailModal';
import { Download, Search, Square, ShoppingCart } from '@/components/icons';
import { orderRef, trackByOrderId } from '@/lib/orderRef';

const STAGE_TONE: Record<string, 'muted' | 'accent' | 'warning' | 'success' | 'danger' | 'info'> = {
  active: 'accent',
  'credit-pending': 'warning',
  'credit-approved': 'success',
  'credit-rejected': 'danger',
  fulfilled: 'success',
  cancelled: 'muted',
};

const CREDIT_TONE = { GREEN: 'success', AMBER: 'warning', RED: 'danger' } as const;

const CHANNELS: [string, string][] = [['', 'All'], ['online', 'Online'], ['regular', 'Regular']];

export function DispatchOrderPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const ask = usePrompt();
  const [stage, setStage] = useState('');
  const [channel, setChannel] = useState('');
  const [partial, setPartial] = useState<'' | 'yes' | 'no'>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewId, setViewId] = useState<number | null>(null);

  // DO date — All / Date (from–to) / Month / Quarter / Year.
  const period = usePeriod();
  const { range } = period;
  useEffect(() => setPage(1), [range]);
  const filter = useMemo(
    () => ({
      stage: stage || undefined,
      channel: channel || undefined,
      partial: partial || undefined,
      search: search || undefined,
      from: range?.from,
      to: range?.to,
      page,
      pageSize,
    }),
    [stage, channel, partial, search, range, page, pageSize],
  );

  const query = useQuery({
    queryKey: ['do', filter],
    queryFn: () => listDos(filter),
    placeholderData: keepPreviousData,
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => cancelDo(id, reason),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['do'] }); toast.push('DO cancelled', 'info'); },
    onError: (e) => toast.push(apiError(e, 'Cancel failed'), 'error'),
  });

  async function onCancel(d: DispatchOrder) {
    const reason = await ask({ title: `Cancel ${d.doNo}?`, label: 'Reason', multiline: true, tone: 'danger', confirmLabel: 'Cancel DO' });
    if (!reason?.trim()) return;
    cancelMutation.mutate({ id: d.id, reason });
  }

  const data = query.data?.data ?? [];
  const total = query.data?.total ?? 0;

  return (
    <div>
      <SectionHeader
        title="Dispatch Orders"      />

      <div className="mb-3 flex gap-1">
        {CHANNELS.map(([v, label]) => (
          <button
            key={v}
            onClick={() => { setChannel(v); setPage(1); }}
            className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${channel === v ? 'bg-accent text-white' : 'bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'}`}
          >
            {label === 'Online' ? <><ShoppingCart size={12} className="mr-1 inline-block align-[-2px]" /> Online</> : label}
          </button>
        ))}
      </div>

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search"
              className="!pl-5 sm:!pl-8 sm:min-w-[260px]"
              aria-label="Search dispatch orders"
            />
          </div>
        </FilterField>
        <FilterField label="Stage">
          <Select value={stage} onChange={(e) => { setStage(e.target.value); setPage(1); }}>
            <option value="">All</option>
            <option>active</option>
            <option value="credit-pending">credit-pending</option>
            <option value="credit-approved">credit-approved</option>
            <option value="credit-rejected">credit-rejected</option>
            <option>fulfilled</option>
            <option>cancelled</option>
          </Select>
        </FilterField>
        <FilterField label="Partial">
          <Select value={partial} onChange={(e) => { setPartial(e.target.value as '' | 'yes' | 'no'); setPage(1); }} aria-label="Filter by partial approval">
            <option value="">All</option>
            <option value="yes">Partially approved</option>
            <option value="no">Fully approved</option>
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        {/* Action chips live inside the filter block on both viewports.
            Mobile: Export in col-1, Promote spans col-2+col-3 of row 2.
            Desktop: right-pinned via ml-auto. */}
        <button
          type="button"
          onClick={() => downloadExcel(dosExportPath(filter), `GRHAMOY_DispatchOrders${partial === 'yes' ? '_Partially_approved' : partial === 'no' ? '_Fully_approved' : ''}.xlsx`)}
          title="Export current filtered list to Excel"
          className="inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-success hover:text-success sm:ml-auto sm:h-8 sm:self-end sm:gap-1.5 sm:px-3 sm:text-xs"
        >
          <Download size={12} /> Export
        </button>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="col-span-2 inline-flex h-7 items-center justify-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:col-span-1 sm:h-8 sm:self-end sm:gap-1.5 sm:px-3 sm:text-xs"
        >
          ＋ Raise DO
        </button>
      </FilterBar>

      <Table<DispatchOrder>
        storageKey="dispatch-order"
        title="Dispatch Orders"
        count={`Showing ${data.length} of ${total}`}
        rows={data}
        rowKey={(r) => r.id}
        loading={query.isFetching && data.length === 0}
        columns={trackByOrderId(channel === 'online', { key: 'orderId', header: 'Order ID', cell: (r) => <span className="font-extrabold text-accent">{orderRef(r.enquiry)}</span> }, ['doNo','enq','pick'], [
          { key: 'doNo', header: 'DO #', cell: (r) => <span className="font-extrabold text-accent">{r.doNo}</span> },
          { key: 'date', header: 'Date', cell: (r) => formatDate(r.doDate) },
          { key: 'enq', header: 'Order Ref', cell: (r) => orderRef(r.enquiry) },
          { key: 'party', header: 'Customer', cell: (r) => <span className="font-bold">{r.party.name}</span> },
          { key: 'expected', header: 'Expected', cell: (r) => formatDate(r.expectedDispatch) },
          { key: 'partial', header: 'Partial', cell: (r) => (r.isPartial ? <Square size={12} className="inline-block align-[-1px] text-warning" /> : '—') },
          { key: 'items', header: 'Items', align: 'right', cell: (r) => r.items.length },
          {
            key: 'qty',
            header: 'Qty',
            align: 'right',
            cell: (r) => r.items.reduce((sum, it) => sum + Number(it.qty), 0),
          },
          { key: 'value', header: 'Value', align: 'right', cell: (r) => formatINRSymbol(r.totalValue) },
          {
            key: 'credit',
            header: 'Credit',
            cell: (r) => r.creditStatus ? <Badge tone={CREDIT_TONE[r.creditStatus]}>{r.creditStatus}</Badge> : '—',
          },
          {
            key: 'stage',
            header: 'Stage',
            cell: (r) => <Badge tone={STAGE_TONE[r.stage] ?? 'muted'}>{r.stage}</Badge>,
          },
          {
            key: 'pick',
            header: 'Pick',
            cell: (r) => (r.pickLists && r.pickLists.length > 0 ? r.pickLists[0].pickNo : '—'),
          },
          {
            key: 'actions',
            header: '',
            width: '160px',
            cell: (r) => (
              <div className="flex flex-nowrap items-center gap-1.5">
                <Button variant="glass" size="sm" onClick={() => setViewId(r.id)}>View</Button>
                {r.stage !== 'cancelled' && r.stage !== 'fulfilled' && (
                  <Button variant="danger" size="sm" onClick={() => onCancel(r)}>Cancel</Button>
                )}
              </div>
            ),
          },
        ])}
        footer={<Pagination page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />}
      />

      <PromoteEnquiryModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <DoDetailModal open={viewId !== null} doId={viewId} onClose={() => setViewId(null)} />
    </div>
  );
}
